import React, { useMemo, useState } from "react";
import {
  Box, Typography, TextField, InputAdornment, Skeleton, Button, Tabs, Tab,
  useTheme, useMediaQuery, alpha, Pagination, Stack, Chip, Drawer, IconButton,
  Snackbar, Alert, Divider,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  useGetMyParentRequestsQuery,
  useGetMyParentRequestDetailQuery,
  useHandleMyParentRequestMutation,
} from "../../Api/attoDeskApi";
import useUser from "../../Core/hook/useUser";
import { C } from "./shared/palette";
import { KpiCard } from "./shared/KpiCard";

/**
 * Staff Portal → Parent Requests.
 *
 * Backend enforces `class_occurrences.staff_id === user.staffId`
 * on every row and every handle call via StaffTeachingScopeService.
 * Client tabs/filters are UX only; the same query re-runs server-side.
 */

type ReqRow = {
  request_id: number;
  request_type: "absent" | "makeup" | "reschedule";
  status: string;
  student: { id: number; name: string; grade: string | null } | null;
  parent_name: string | null;
  occurrence_id: number | null;
  class_label: string;
  requested_date: string | null;
  requested_time: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  location: string | null;
  reason: string | null;
  admin_notes: string | null;
  created_at: string | null;
  handled_at: string | null;
  action_state: "actionable" | "closed";
};

type TabKey = "needs_action" | "approved" | "rejected" | "all";

// Status → chip visual mapping, aligned with the shared palette.
const STATUS_META: Record<string, { tone: keyof typeof C; label: string }> = {
  pending:              { tone: "amber",  label: "Needs Action" },
  alternative_proposed: { tone: "indigo", label: "Alternative Proposed" },
  approved:             { tone: "green",  label: "Approved" },
  scheduled:            { tone: "green",  label: "Scheduled" },
  rejected:             { tone: "rose",   label: "Rejected" },
  cancelled:            { tone: "navy",   label: "Cancelled" },
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const meta = STATUS_META[status] ?? { tone: "navy" as const, label: status };
  const t = C[meta.tone];
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        height: 22,
        fontSize: 10.5,
        fontWeight: 800,
        bgcolor: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
      }}
    />
  );
};

const TYPE_META: Record<string, { tone: keyof typeof C; label: string; Icon: React.ElementType }> = {
  absent:     { tone: "blue",   label: "Absence",    Icon: EventBusyOutlinedIcon },
  makeup:     { tone: "purple", label: "Makeup",     Icon: EventAvailableOutlinedIcon },
  reschedule: { tone: "indigo", label: "Reschedule", Icon: EventRepeatOutlinedIcon },
};

const TypeChip: React.FC<{ type: string }> = ({ type }) => {
  const meta = TYPE_META[type] ?? { tone: "navy" as const, label: type, Icon: MarkEmailUnreadOutlinedIcon };
  const t = C[meta.tone];
  return (
    <Chip
      size="small"
      icon={<meta.Icon sx={{ fontSize: 14 }} />}
      label={meta.label}
      sx={{
        height: 22,
        fontSize: 10.5,
        fontWeight: 800,
        bgcolor: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        "& .MuiChip-icon": { color: t.fg },
      }}
    />
  );
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

// Tab visual metadata — active colour per tab per spec.
const TAB_TONES: Record<TabKey, keyof typeof C> = {
  needs_action: "amber",
  approved:     "green",
  rejected:     "rose",
  all:          "blue",
};

const ParentRequests: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userHasRight } = useUser();
  const canManage = userHasRight("ParentRequest_Manage");
  const [tab, setTab] = useState<TabKey>("needs_action");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [openId, setOpenId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; sev: "success" | "error" } | null>(null);

  const { data, isLoading, isFetching } = useGetMyParentRequestsQuery({
    status: tab === "all" ? undefined : tab,
    search: search || undefined,
    page, per_page: perPage,
  });
  const rows: ReqRow[] = useMemo(() => (data as any)?.data?.rows ?? [], [data]);
  const total: number = (data as any)?.data?.total ?? 0;
  const counts = (data as any)?.data?.counts ?? { needs_action: 0, absence: 0, makeup: 0 };
  const pageCount = Math.max(1, Math.ceil(total / perPage));

  // Server returns per-tab totals but not per-status totals for the
  // Approved/Rejected tab badges; only surface counts we actually have.
  const tabCount = (k: TabKey): number | null => {
    if (k === "needs_action") return counts.needs_action ?? 0;
    if (k === "all") return null;
    return null;
  };

  const activeToneColor = C[TAB_TONES[tab]].fg;
  const needsAction: number = counts.needs_action ?? 0;
  const absenceCount: number = counts.absence ?? 0;
  const makeupCount: number = counts.makeup ?? 0;

  const hasActiveFilters = Boolean(search) || tab !== "needs_action";
  const clearFilters = () => { setSearch(""); setTab("needs_action"); setPage(1); };

  return (
    <Box sx={{ bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1720, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        {/* Compact premium header */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "18px",
            px: { xs: 2.5, md: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
            flexWrap: "wrap",
            border: "1px solid rgba(15,23,42,0.06)",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            background: "linear-gradient(135deg, #ffffff 0%, #fafcff 55%, #f4f0ff 100%)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: "12px",
                bgcolor: C.purple.bg, color: C.purple.fg,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MarkEmailUnreadOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "1.4px", color: "#64748b", textTransform: "uppercase" }}>
                Staff Portal
              </Typography>
              <Typography sx={{ fontSize: { xs: 20, md: 26 }, fontWeight: 800, letterSpacing: "-0.02em", color: C.navy.fg, lineHeight: 1.2 }}>
                Parent Requests
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                Review absence and makeup requests from parents.
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: "999px",
              bgcolor: needsAction > 0 ? C.amber.bg : C.green.bg,
              color: needsAction > 0 ? C.amber.fg : C.green.fg,
              border: `1px solid ${needsAction > 0 ? C.amber.border : C.green.border}`,
              fontWeight: 800,
              fontSize: 12.5,
            }}
          >
            {needsAction > 0 ? (
              <NotificationsActiveOutlinedIcon sx={{ fontSize: 16 }} />
            ) : (
              <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
            )}
            {isLoading
              ? "Loading…"
              : needsAction > 0
                ? `${needsAction} request${needsAction === 1 ? "" : "s"} need${needsAction === 1 ? "s" : ""} attention`
                : "All caught up"}
          </Box>
        </Box>

        {/* KPI row — shared KpiCard primitive */}
        <Box sx={{ display: "grid", gap: 1.5, mb: 2.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }}>
          <KpiCard
            tone="amber"
            Icon={NotificationsActiveOutlinedIcon}
            label="Needs Action"
            helper="Awaiting review"
            value={needsAction}
            loading={isLoading}
          />
          <KpiCard
            tone="blue"
            Icon={EventBusyOutlinedIcon}
            label="Absence"
            helper="Absence requests"
            value={absenceCount}
            loading={isLoading}
          />
          <KpiCard
            tone="purple"
            Icon={EventAvailableOutlinedIcon}
            label="Makeup"
            helper="Makeup requests"
            value={makeupCount}
            loading={isLoading}
          />
        </Box>

        {/* Workspace card */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "18px",
            border: "1px solid rgba(15,23,42,0.06)",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            overflow: "hidden",
          }}
        >
          {/* Tabs (pill style with per-tab active colour) */}
          <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1.5 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => { setTab(v as TabKey); setPage(1); }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                sx: { display: "none" },
              }}
              sx={{
                minHeight: 40,
                "& .MuiTabs-flexContainer": { gap: 0.75 },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 13,
                  minHeight: 36,
                  py: 0.75,
                  px: 1.75,
                  borderRadius: "999px",
                  color: "#475569",
                  bgcolor: "#f1f5f9",
                  border: "1px solid rgba(15,23,42,0.06)",
                  transition: "background-color 160ms ease, color 160ms ease, border-color 160ms ease",
                },
                "& .MuiTab-root:hover": {
                  bgcolor: "#e2e8f0",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: activeToneColor,
                  bgcolor: C[TAB_TONES[tab]].bg,
                  borderColor: C[TAB_TONES[tab]].border,
                },
              }}
            >
              {(["needs_action", "approved", "rejected", "all"] as TabKey[]).map((k) => {
                const label = k === "needs_action" ? "Needs Action"
                  : k === "approved" ? "Approved"
                    : k === "rejected" ? "Rejected"
                      : "All";
                const c = tabCount(k);
                const full = c !== null ? `${label} ${c}` : label;
                return <Tab key={k} value={k} label={full} />;
              })}
            </Tabs>
          </Box>

          {/* Search bar */}
          <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1.5, pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by student, parent or reason…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: "#F1F5F9",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "rgba(15,23,42,0.12)" },
                  "&.Mui-focused fieldset": { borderColor: C.blue.fg, borderWidth: 1 },
                  "&.Mui-focused": { bgcolor: "#fff" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")} aria-label="Clear search">
                      <ClearOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Box>

          {/* List */}
          {isLoading || isFetching ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rounded" height={72} sx={{ mb: 1, borderRadius: "12px" }} />
              <Skeleton variant="rounded" height={72} sx={{ mb: 1, borderRadius: "12px" }} />
              <Skeleton variant="rounded" height={72} sx={{ borderRadius: "12px" }} />
            </Box>
          ) : rows.length === 0 ? (
            <Box
              sx={{
                minHeight: 280,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 3, py: 4,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", maxWidth: 380 }}>
                <Box
                  sx={{
                    width: 72, height: 72, borderRadius: "50%",
                    bgcolor: hasActiveFilters ? C.blue.bg : C.green.bg,
                    color: hasActiveFilters ? C.blue.fg : C.green.fg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    mb: 1.75,
                    boxShadow: `0 0 0 8px ${hasActiveFilters ? alpha("#3b82f6", 0.05) : alpha("#22c55e", 0.05)}`,
                  }}
                >
                  {hasActiveFilters
                    ? <SearchOutlinedIcon sx={{ fontSize: 34 }} />
                    : <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 34 }} />}
                </Box>
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: C.navy.fg }}>
                  {hasActiveFilters ? "No matches" : "All caught up"}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
                  {hasActiveFilters
                    ? "Try a different search term or switch tabs."
                    : "No parent requests need your attention right now."}
                </Typography>
                {!hasActiveFilters && (
                  <Typography sx={{ fontSize: 12.5, color: "text.disabled", mt: 0.5 }}>
                    New absence or makeup requests will appear here.
                  </Typography>
                )}
                {hasActiveFilters && (
                  <Button
                    size="small"
                    onClick={clearFilters}
                    startIcon={<ClearOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      mt: 1.5,
                      textTransform: "none",
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: C.blue.fg,
                      "&:hover": { bgcolor: alpha("#3b82f6", 0.08) },
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ px: { xs: 1, md: 1.5 }, pb: 1.5 }}>
              {rows.map((r) => (
                <RequestRow
                  key={r.request_id}
                  row={r}
                  canManage={canManage}
                  isMobile={isMobile}
                  onOpen={() => setOpenId(r.request_id)}
                />
              ))}
            </Box>
          )}

          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1.5, borderTop: "1px solid #f1f5f9" }}>
              <Pagination page={page} count={pageCount} onChange={(_, p) => setPage(p)} size="small" />
            </Box>
          )}
        </Box>
      </Box>

      <RequestDrawer
        id={openId}
        canManage={canManage}
        onClose={() => setOpenId(null)}
        onDecided={(status) => {
          setOpenId(null);
          setToast({ msg: `Request ${status}.`, sev: "success" });
        }}
        onError={(m) => setToast({ msg: m, sev: "error" })}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast?.sev ?? "success"} variant="filled" onClose={() => setToast(null)} sx={{ fontWeight: 700 }}>
          {toast?.msg ?? ""}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════
// Row card — LEFT (type icon + status), CENTER (student/parent/date/reason),
// RIGHT (action buttons). Whole row is still clickable; buttons stop
// propagation so they don't double-open the drawer.
const RequestRow: React.FC<{
  row: ReqRow;
  canManage: boolean;
  isMobile: boolean;
  onOpen: () => void;
}> = ({ row, canManage, isMobile, onOpen }) => {
  const meta = TYPE_META[row.request_type] ?? { tone: "navy" as const, label: row.request_type, Icon: MarkEmailUnreadOutlinedIcon };
  const tone = C[meta.tone];
  const isActionable = row.action_state === "actionable" && canManage;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      sx={{
        display: "flex",
        alignItems: { xs: "stretch", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 1.25, sm: 1.75 },
        px: { xs: 1.25, md: 1.75 },
        py: 1.5,
        mt: 1,
        borderRadius: "14px",
        bgcolor: "#fff",
        border: "1px solid rgba(15,23,42,0.06)",
        cursor: "pointer",
        transition: "box-shadow 160ms ease, transform 160ms ease, border-color 160ms ease",
        "&:hover": {
          boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
          borderColor: tone.border,
          transform: "translateY(-1px)",
        },
        "&:focus-visible": {
          outline: `2px solid ${tone.fg}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* LEFT: type icon + type label + status chip */}
      <Stack
        direction={{ xs: "row", sm: "row" }}
        spacing={1}
        alignItems="center"
        sx={{ minWidth: { sm: 200 }, flexShrink: 0 }}
      >
        <Box
          sx={{
            width: 40, height: 40, borderRadius: "10px",
            bgcolor: tone.bg, color: tone.fg,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <meta.Icon />
        </Box>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: tone.fg, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {meta.label}
          </Typography>
          <StatusChip status={row.status} />
        </Stack>
      </Stack>

      {/* CENTER: student, parent, request date, reason */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: C.navy.fg, lineHeight: 1.25 }}>
          {row.student?.name ?? "—"}
          {row.student?.grade ? (
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", ml: 0.75 }}>
              · {row.student.grade}
            </Typography>
          ) : null}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
          {row.parent_name ? `${row.parent_name} · ` : ""}
          {row.class_label}
          {row.scheduled_date ? ` · ${fmtDate(row.scheduled_date)}` : ""}
          {row.scheduled_time ? ` · ${row.scheduled_time}` : ""}
        </Typography>
        {row.reason && (
          <Typography
            sx={{
              fontSize: 12.5,
              color: "#475569",
              mt: 0.5,
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {row.reason}
          </Typography>
        )}
        <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.5 }}>
          Submitted {fmtDate(row.created_at)}
        </Typography>
      </Box>

      {/* RIGHT: action buttons — always show View Details; Approve/Reject only when actionable.
          NOTE: Reject/Approve here open the detail drawer (which already
          hosts the notes textarea + confirmation flow). We do NOT
          silently approve/reject from the row — the existing drawer
          flow with optional notes is preserved. */}
      <Stack
        direction={{ xs: "row", sm: "row" }}
        spacing={0.75}
        sx={{
          flexShrink: 0,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          alignSelf: { xs: "stretch", sm: "center" },
          justifyContent: { xs: "flex-end", sm: "flex-start" },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isActionable && (
          <>
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={onOpen}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                fontSize: 12.5,
                borderRadius: "10px",
                bgcolor: "#16A34A",
                boxShadow: "none",
                "&:hover": { bgcolor: "#15803d", boxShadow: "0 4px 10px rgba(22,163,74,0.25)" },
              }}
            >
              {isMobile ? "" : "Approve"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={onOpen}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                fontSize: 12.5,
                borderRadius: "10px",
                color: "#DC2626",
                borderColor: alpha("#DC2626", 0.4),
                "&:hover": { borderColor: "#DC2626", bgcolor: alpha("#DC2626", 0.06) },
              }}
            >
              {isMobile ? "" : "Reject"}
            </Button>
          </>
        )}
        <Button
          size="small"
          variant="text"
          startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={onOpen}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: 12.5,
            borderRadius: "10px",
            color: C.blue.fg,
            "&:hover": { bgcolor: alpha("#3b82f6", 0.08) },
          }}
        >
          {isMobile ? "Details" : "View Details"}
        </Button>
      </Stack>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════
const RequestDrawer: React.FC<{
  id: number | null;
  canManage: boolean;
  onClose: () => void;
  onDecided: (status: "approved" | "rejected") => void;
  onError: (m: string) => void;
}> = ({ id, canManage, onClose, onDecided, onError }) => {
  const { data, isLoading } = useGetMyParentRequestDetailQuery({ id: id ?? 0 }, { skip: !id });
  const r: ReqRow | undefined = (data as any)?.data as ReqRow | undefined;
  const [handle, { isLoading: handling }] = useHandleMyParentRequestMutation();
  const [notes, setNotes] = useState("");

  const decide = async (status: "approved" | "rejected") => {
    if (!id) return;
    try {
      await handle({ id, status, admin_notes: notes.trim() || undefined }).unwrap();
      onDecided(status);
    } catch (err: any) {
      const s = err?.status ?? err?.originalStatus;
      const msg = err?.data?.message;
      if (s === 403) onError(msg || "You don't have access to this request.");
      else if (s === 409) onError(msg || "This request is no longer actionable.");
      else onError(msg || "Failed to update the request.");
    }
  };

  return (
    <Drawer
      anchor="right"
      open={!!id}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 480 }, bgcolor: "#F8FAFC" } }}
    >
      <Box
        sx={{
          px: 2, py: 1.5,
          borderBottom: "1px solid rgba(15,23,42,0.08)",
          bgcolor: "#fff",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <ArrowBackOutlinedIcon />
            </IconButton>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.navy.fg }}>Parent Request</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} aria-label="Dismiss"><CloseIcon /></IconButton>
        </Stack>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
        {isLoading || !r ? (
          <>
            <Skeleton height={30} sx={{ mb: 1 }} />
            <Skeleton height={20} sx={{ mb: 1 }} />
            <Skeleton height={200} />
          </>
        ) : (
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "14px",
              border: "1px solid rgba(15,23,42,0.06)",
              p: 2.25,
            }}
          >
            <Stack direction="row" spacing={0.75} sx={{ mb: 1.25, flexWrap: "wrap", gap: 0.5 }}>
              <TypeChip type={r.request_type} />
              <StatusChip status={r.status} />
            </Stack>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.navy.fg }}>{r.student?.name}</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{r.student?.grade ?? ""}</Typography>
            <Divider sx={{ my: 2 }} />
            <Row label="Class" value={r.class_label} />
            <Row label="Scheduled" value={[r.scheduled_date && fmtDate(r.scheduled_date), r.scheduled_time].filter(Boolean).join(" · ")} />
            {r.location && <Row label="Location" value={r.location} />}
            {r.requested_date && <Row label="Requested date" value={fmtDate(r.requested_date)} />}
            {r.requested_time && <Row label="Requested time" value={r.requested_time} />}
            {r.parent_name && <Row label="Parent / Guardian" value={r.parent_name} />}
            {r.reason && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px", color: "text.secondary" }}>Reason</Typography>
                <Typography sx={{ fontSize: 13, mt: 0.5, whiteSpace: "pre-wrap", color: "#334155" }}>{r.reason}</Typography>
              </Box>
            )}
            {r.admin_notes && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px", color: "text.secondary" }}>Staff notes</Typography>
                <Typography sx={{ fontSize: 13, mt: 0.5, whiteSpace: "pre-wrap", color: "#334155" }}>{r.admin_notes}</Typography>
              </Box>
            )}
            {canManage && r.action_state === "actionable" && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth multiline minRows={2} size="small" label="Notes (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{
                    bgcolor: "#fff",
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                  }}
                />
                <Stack direction="row" spacing={1.25} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined" fullWidth
                    startIcon={<CancelOutlinedIcon />}
                    disabled={handling}
                    onClick={() => decide("rejected")}
                    sx={{
                      textTransform: "none", fontWeight: 800, borderRadius: "10px",
                      color: "#DC2626", borderColor: alpha("#DC2626", 0.4),
                      "&:hover": { borderColor: "#DC2626", bgcolor: alpha("#DC2626", 0.06) },
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained" fullWidth
                    startIcon={<CheckCircleOutlineIcon />}
                    disabled={handling}
                    onClick={() => decide("approved")}
                    sx={{
                      textTransform: "none", fontWeight: 800, borderRadius: "10px",
                      bgcolor: "#16A34A", boxShadow: "none",
                      "&:hover": { bgcolor: "#15803d" },
                    }}
                  >
                    Approve
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.75 }}>
    <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: "right", color: C.navy.fg }}>{value || "—"}</Typography>
  </Stack>
);

export default ParentRequests;
